import styled, { css } from 'styled-components';
import ndsTokens, { breakpoints, space } from 'nicollet-tokens';

const BREAKPOINTS = {
  xs: '0',
  sm: breakpoints.sm,
  md: breakpoints.md,
  lg: breakpoints.lg,
  xl: breakpoints.xl
};
const SPACING = {
  tiny: space.generic.x1,
  tight: space.generic.x2,
  default: space.generic.x3,
  wide: space.generic.x4,
  jumbo: space.generic.x5
};

const percentage = value => `${value * 100}%`;

const removePx = str => str ? str.replace('px', '') : str;

const blockColumnStyles = (blockColumnCount, breakpoint) => {
  if (!blockColumnCount) return null;
  const columnCount = typeof blockColumnCount === 'string' ? Number.parseInt(blockColumnCount, 10) : blockColumnCount;
  const value = percentage(1 / columnCount);
  return css`
    @media only screen and (min-width: ${BREAKPOINTS[`${breakpoint}`]}) {
      & > * {
        /* Allow for spacing between elements without needing internal element */
        flex-basis: ${value} !important;
        max-width: ${value} !important;
      }
    }
  `;
};
const gutterStyles = (gutterWidth, breakpoint) => {
  if (!gutterWidth) return null;

  if (gutterWidth === true) {
    gutterWidth = 'default'; // eslint-disable-line no-param-reassign
  }

  const gutterSize = Number(removePx(SPACING[gutterWidth]));
  const halfGutter = gutterSize / 2;
  return css`
    @media only screen and (min-width: ${BREAKPOINTS[`${breakpoint}`]}) {
      margin-left: -${halfGutter}px;
      margin-right: -${halfGutter}px;

      & > * {
        padding-left: ${halfGutter}px !important;
        padding-right: ${halfGutter}px !important;
      }
    }
  `;
};
const dividerStyles = (divider, breakpoint) => {
  if (!divider) return null;
  return css`
    border-right-color: ${ndsTokens.colors.border.container.default};

    @media only screen and (min-width: ${BREAKPOINTS[`${breakpoint}`]}) {
      & > * {
        border-right-width: 1px;
        border-right-style: solid;
        border-right-color: inherit;

        &:last-child {
          border-right-width: 0;
        }
      }
    }
  `;
};
const ALIGNMENT_MAP = {
  start: 'justify-content: flex-start; text-align: start',
  center: 'justify-content: center; text-align: center',
  end: 'justify-content: flex-end; text-align: end',
  top: 'align-items: flex-start',
  middle: 'align-items: center',
  bottom: 'align-items: flex-end',
  around: 'justify-content: space-around',
  between: 'justify-content: space-between',
  first: 'order: -1',
  last: 'order: 1'
};

const createAlignmentMediaQuery = alignment => `
    @media 
    ${alignment.min && `(min-width: ${alignment.min})`}
    ${alignment.max && ` and (max-width: ${alignment.max})`} 
      { 
        ${alignment.alignment};
      }
  `;

const alignmentStyles = () => css`
  ${props => {
  const alignmentsToApply = {};
  const alignmentPriority = ['xs', 'sm', 'md', 'lg', 'xl']; // Find max width for an alignment based on other alignments passed in

  const getMaxWidth = alignmentBreakpoint => {
    let max = '';
    const startAt = alignmentPriority.indexOf(alignmentBreakpoint);

    for (let i = startAt + 1; i < alignmentPriority.length; i++) {
      if (alignmentsToApply[alignmentPriority[i]]) {
        max = alignmentPriority[i - 1];
        return max;
      }
    }

    return max;
  };

  Object.keys(ALIGNMENT_MAP).forEach(alignment => {
    var _a;

    if (props[alignment]) {
      const maxWidth = getMaxWidth(props[alignment]);
      alignmentsToApply[props[alignment]] = {
        alignment: ALIGNMENT_MAP[alignment],
        min: BREAKPOINTS[props[alignment]],
        max: (_a = breakpoints[`${maxWidth}Max`]) !== null && _a !== void 0 ? _a : ''
      };
    }
  });
  return `
      ${alignmentsToApply.xs && createAlignmentMediaQuery(alignmentsToApply.xs)};
      ${alignmentsToApply.sm && createAlignmentMediaQuery(alignmentsToApply.sm)};
      ${alignmentsToApply.md && createAlignmentMediaQuery(alignmentsToApply.md)};
      ${alignmentsToApply.lg && createAlignmentMediaQuery(alignmentsToApply.lg)};
      ${alignmentsToApply.xl && createAlignmentMediaQuery(alignmentsToApply.xl)};
    `;
}}
`;
const StyledRow = styled.div.withConfig({
  /**
   * 'wrap' & 'fill' are valid HTML Attributes, so we need to filter them out
   * defaultValidatorFn removes any other prop names that would be invalid HTML attributes
   */
  shouldForwardProp: (prop, defaultValidatorFn) => !['wrap', 'fill'].includes(prop) && defaultValidatorFn(prop)
})`
  box-sizing: border-box;
  display: flex;
  flex: 0 1 auto;
  flex-direction: ${props => {
  var _a;

  return (_a = props.direction) !== null && _a !== void 0 ? _a : 'row';
}};
  flex-wrap: ${props => {
  var _a;

  return (_a = props.wrap) !== null && _a !== void 0 ? _a : 'wrap';
}};

  ${({
  fill
}) => fill && `
    height: 100%;
    width: 100%;
  `};

  ${alignmentStyles()}

  ${({
  xsBlock
}) => xsBlock && blockColumnStyles(xsBlock, 'xs')};
  ${({
  smBlock
}) => smBlock && blockColumnStyles(smBlock, 'sm')};
  ${({
  mdBlock
}) => mdBlock && blockColumnStyles(mdBlock, 'md')};
  ${({
  lgBlock
}) => lgBlock && blockColumnStyles(lgBlock, 'lg')};
  ${({
  xlBlock
}) => xlBlock && blockColumnStyles(xlBlock, 'xl')};
  ${({
  xsGutter
}) => xsGutter && gutterStyles(xsGutter, 'xs')};
  ${({
  smGutter
}) => smGutter && gutterStyles(smGutter, 'sm')};
  ${({
  mdGutter
}) => mdGutter && gutterStyles(mdGutter, 'md')};
  ${({
  lgGutter
}) => lgGutter && gutterStyles(lgGutter, 'lg')};
  ${({
  xlGutter
}) => xlGutter && gutterStyles(xlGutter, 'xl')};
  ${({
  xsDividers
}) => xsDividers && dividerStyles(xsDividers, 'xs')};
  ${({
  smDividers
}) => smDividers && dividerStyles(smDividers, 'sm')};
  ${({
  mdDividers
}) => mdDividers && dividerStyles(mdDividers, 'md')};
  ${({
  lgDividers
}) => lgDividers && dividerStyles(lgDividers, 'lg')};
  ${({
  xlDividers
}) => xlDividers && dividerStyles(xlDividers, 'xl')};
`;

export { StyledRow, alignmentStyles, blockColumnStyles, dividerStyles, gutterStyles };
